import { AutocompleteOption, LocationAutocomplete } from '@checkit/react-components';
import { Box, ButtonGroup, Divider, List, ListItem, ListItemSecondaryAction, ListItemText } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import ClearIcon from '@material-ui/icons/Clear';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { SelectedAction } from '../checkpointSelectionReducer';
import { messages } from '../list/CheckpointList.translations';
import { Checkpoint } from '../state';

const drawerWidth = 240;
const useStyles = makeStyles((theme: Theme) => ({
  box: {
    marginTop: theme.spacing(2)
  },
  button: {
    marginTop: theme.spacing(2)
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  dividerFullWidth: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  drawer: {
    flexShrink: 0,
    width: drawerWidth
  },
  drawerPaper: {
    width: drawerWidth
  },
  formControl: {
    margin: theme.spacing(1)
  },
  list: {
    height: 250,
    overflow: 'auto'
  },
  section1: {
    margin: theme.spacing(3, 2)
  }
}));

interface CheckpointNavBarProps {
  selectedCheckpoints: Checkpoint[];
  open: boolean;
  dispatchSelected: (action: SelectedAction) => void;
  handleAssignCheckpoints: (location: string | null) => void;
}

type CheckpointListProps = Pick<CheckpointNavBarProps, 'selectedCheckpoints' | 'dispatchSelected'>;

const CheckpointList: React.FC<CheckpointListProps> = (props: CheckpointListProps) => {
  const { formatMessage } = useIntl();
  const { selectedCheckpoints, dispatchSelected } = props;
  const classes = useStyles();

  const handleRemoveCheckpoint = (checkpoint: Checkpoint) => {
    dispatchSelected({ type: 'remove', values: [checkpoint] });
  };

  const handleClearAllCheckpoints = () => {
    dispatchSelected({ type: 'clear' });
  };

  return (
    <Box width={1} className={classes.box} >
      <Typography gutterBottom={true} variant="subtitle1">
        {formatMessage(messages['checkpoints.navbar.selectedCheckpoints'])}
      </Typography>
      <List dense={true} className={classes.list}>
        {
          selectedCheckpoints.map(checkpoint => {
            return (
              <ListItem key={checkpoint.code}>
                <ListItemText
                  primary={checkpoint.name}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="clear"
                    onClick={handleRemoveCheckpoint.bind(null, checkpoint)}
                  >
                    <ClearIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })
        }
      </List>
      <Grid container={true} alignItems="flex-start" justify="flex-end" direction="row">
        <Button
          className={classes.button}
          onClick={handleClearAllCheckpoints}
          variant="outlined"
          size="small"
        >
          {formatMessage(messages['checkpoints.navbar.clearAll'])}
        </Button>
      </Grid>
    </Box>
  );
};

type DrawerContentProps = Pick<CheckpointNavBarProps, 'selectedCheckpoints' | 'dispatchSelected' | 'handleAssignCheckpoints'>;

const DrawerContent: React.FC<DrawerContentProps> = (props: DrawerContentProps) => {
  const { formatMessage } = useIntl();
  const { selectedCheckpoints, dispatchSelected, handleAssignCheckpoints } = props;
  const classes = useStyles();

  const [location, setLocation] = useState<string | null>(null);

  const onLocationChange = (event: React.ChangeEvent<{}>, value: AutocompleteOption | null) => {
    setLocation(value ? value.code : null);
  };

  return (
    <>
      <Toolbar />
      <div className={classes.section1}>
        <Grid container={true} alignItems="center">
          <Typography gutterBottom={true} variant="h6">
            {formatMessage(messages['checkpoints.navbar.bulkActions'])}
          </Typography>
          <CheckpointList
            selectedCheckpoints={selectedCheckpoints}
            dispatchSelected={dispatchSelected}
          />
          <Box width={1} className={classes.box}>
            <Divider className={classes.dividerFullWidth} />
            <Typography variant="subtitle1">
              {formatMessage(messages['checkpoints.navbar.assignTitle'])}
            </Typography>
            <LocationAutocomplete
              onChange={onLocationChange}
              label={formatMessage(messages['checkpoints.navbar.selectedLocation'])}
            />
            <Grid container={true} alignItems="flex-start" justify="flex-end" direction="row">
              <ButtonGroup variant="contained" className={classes.button} size="small">
                <Button
                  disabled={!location}
                  onClick={handleAssignCheckpoints.bind(null, String(location))}
                  color="primary"
                >
                  {formatMessage(messages['checkpoints.navbar.assign'])}
                </Button>
              </ButtonGroup>
            </Grid>

            <Divider className={classes.dividerFullWidth} />
            <Typography variant="subtitle1">
              {formatMessage(messages['checkpoints.navbar.unassignTitle'])}
            </Typography>
            <Grid container={true} alignItems="flex-start" justify="flex-end" direction="row">
              <ButtonGroup variant="contained" className={classes.button} size="small">
                <Button
                  disabled={!!location}
                  onClick={handleAssignCheckpoints.bind(null, null)}
                  color="secondary"
                >
                  {formatMessage(messages['checkpoints.navbar.unassign'])}
                </Button>
              </ButtonGroup>
            </Grid>
          </Box>
        </Grid>
      </div>
    </>
  );
};

export const CheckpointNavBar: React.FC<CheckpointNavBarProps> = (props: CheckpointNavBarProps) => {
  const { selectedCheckpoints, open, dispatchSelected, handleAssignCheckpoints } = props;
  const classes = useStyles();

  return (
    <nav className={classes.drawer} aria-label="mailbox folders">
      <Drawer
        classes={{ paper: classes.drawerPaper }}
        variant="persistent"
        SlideProps={{
          unmountOnExit: true
        }}
        anchor="right"
        open={open}
      >
        <DrawerContent
          selectedCheckpoints={selectedCheckpoints}
          dispatchSelected={dispatchSelected}
          handleAssignCheckpoints={handleAssignCheckpoints}
        />
      </Drawer>
    </nav>
  );
};
